import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { errorMessage } from "../errors";
import { Icon } from "../icons/Icon";
import { cx } from "../styles";
import { Button } from "./Button";
import { Label } from "./Field";
import { Input } from "./Input";
import { CLOSE_BUTTON, PANEL, SCRIM, TRANSITION } from "./overlay";
import { useUiText } from "./uiText";

/**
 * A centred modal.
 *
 * For a question that has to be answered before anything else happens — a
 * confirmation, a short form, a permission prompt. Anything with more than
 * about four fields belongs in a {@link Drawer}, which can scroll without
 * leaving the page.
 *
 * The title is required rather than optional, and it is required because the
 * dialog is labelled by it. A modal with no accessible name is announced as
 * "dialog" and nothing else, which leaves somebody with a trapped focus ring
 * and no idea what they are being asked.
 */

export type DialogSize = "sm" | "md" | "lg" | "xl";

const SIZES: Record<DialogSize, string> = {
	sm: "max-w-[380px]",
	md: "max-w-[520px]",
	lg: "max-w-[720px]",
	// For a dialog whose body is an editable table (EP02-S10's variations).
	xl: "max-w-[960px]",
};

/**
 * Below 640px the dialog takes the whole screen: no margin, no rounding, the
 * full dynamic height. For a dialog with a table or several steps, where a
 * card floating over a phone's page would leave a column of inputs too narrow
 * to type into.
 */
const MOBILE_FULL_SCREEN =
	"max-sm:start-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 " +
	"max-sm:rtl:translate-x-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-screen " +
	"max-sm:max-w-none max-sm:rounded-none max-sm:border-0";

export interface DialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Names the dialog. Required: it is what `aria-labelledby` points at. */
	title: ReactNode;
	/** A sentence under the title, for context the title cannot carry. */
	description?: ReactNode;
	size?: DialogSize;
	children?: ReactNode;
	/** The actions, end-aligned in a footer. */
	footer?: ReactNode;
	/** The label on the close button, for assistive technology. */
	closeLabel?: string;
	/** Take the whole screen below 640px. See {@link MOBILE_FULL_SCREEN}. */
	fullScreenOnMobile?: boolean;
}

export function Dialog({
	open,
	onOpenChange,
	title,
	description,
	size = "md",
	children,
	footer,
	closeLabel,
	fullScreenOnMobile = false,
}: DialogProps) {
	// The close button's name follows the locale when the caller gives none
	// (ADJ-M03-12): eleven Projects dialogs said "Close" in Arabic.
	const t = useUiText();
	const close = closeLabel ?? t("ui.actions.close");
	return (
		<RadixDialog.Root open={open} onOpenChange={onOpenChange}>
			<RadixDialog.Portal>
				<RadixDialog.Overlay className={cx(SCRIM, TRANSITION)} />
				<RadixDialog.Content
					className={cx(
						PANEL,
						// Centred by translating half its own size, so it does not need
						// a known height — a dialog whose body grows stays centred.
						"start-1/2 top-1/2 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2",
						"flex max-h-[calc(100vh-4rem)] flex-col rounded-lg border border-border",
						SIZES[size],
						fullScreenOnMobile && MOBILE_FULL_SCREEN,
						"data-[state=open]:animate-[km-rise_150ms_ease-out]",
						TRANSITION,
					)}
				>
					<div className="flex items-start gap-3 border-b border-border px-5 py-4">
						<div className="min-w-0 flex-1">
							<RadixDialog.Title className="text-[16px] font-bold tracking-[-0.01em] text-fg">
								{title}
							</RadixDialog.Title>
							{description !== undefined && (
								<RadixDialog.Description className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">
									{description}
								</RadixDialog.Description>
							)}
						</div>
						<RadixDialog.Close aria-label={close} className={CLOSE_BUTTON}>
							<Icon as={X} size={18} />
						</RadixDialog.Close>
					</div>

					{children !== undefined && (
						<div data-scroll="1" className="flex-1 overflow-y-auto px-5 py-4 text-[13px] text-fg">
							{children}
						</div>
					)}

					{footer !== undefined && (
						<div className="flex justify-end gap-2 rounded-b-lg border-t border-border bg-bg-subtle px-5 py-3.5 max-sm:rounded-none">
							{footer}
						</div>
					)}
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

export interface ConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: ReactNode;
	/** What the action will do, in a sentence somebody can act on. */
	description?: ReactNode;
	/** `danger` for anything that destroys or cannot be undone. */
	tone?: "default" | "danger";
	confirmLabel: string;
	cancelLabel: string;
	/**
	 * Runs on confirm. The dialog shows a busy state until it settles and stays
	 * open if it rejects — closing on a failed delete would leave somebody
	 * believing it worked.
	 */
	onConfirm: () => void | Promise<void>;
	/**
	 * Demands that the person type this exact string before Confirm enables.
	 *
	 * For the handful of actions that cannot be undone and affect more than the
	 * person doing them — closing a tenant, deleting a store with stock in it.
	 * The friction is the point: it makes the action impossible to perform by
	 * muscle memory, which is how the dangerous ones actually happen.
	 */
	requireTypedText?: string;
	/** The label on the typed-confirmation field. */
	typedTextLabel?: string;
	/**
	 * One more fact the action needs, above the buttons.
	 *
	 * A reason select on a suspension, a memo on a reversal. Not a second
	 * dialog and not a drawer: the question is "are you sure, and why", which
	 * is one question — splitting it would make somebody confirm twice, and
	 * putting it in a form would lose the confirmation that makes a
	 * one-way action deliberate.
	 */
	children?: ReactNode;
	/**
	 * Holds Confirm until the caller says the extra fields are answered.
	 *
	 * The caller owns that judgement because only it knows what its fields mean
	 * — a required select is not answered by having a value, it is answered by
	 * having the *right* one.
	 */
	confirmDisabled?: boolean;
	/**
	 * The label on the header's ✕.
	 *
	 * Separate from `cancelLabel`, and an earlier draft reused it — which put two
	 * controls with the same accessible name in one dialog, so a screen reader
	 * announced "Keep editing, button" twice with nothing to tell them apart.
	 * They do the same thing; they should still be distinguishable.
	 */
	closeLabel?: string;
	/**
	 * The screen's own messages for a refusal's code (`inventory.categories.errors`),
	 * tried before the shared ones (ADJ-M03-6).
	 */
	errorKeyPrefix?: string;
}

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	tone = "default",
	confirmLabel,
	cancelLabel,
	onConfirm,
	requireTypedText,
	typedTextLabel,
	children,
	confirmDisabled = false,
	closeLabel,
	errorKeyPrefix,
}: ConfirmDialogProps) {
	const t = useUiText();
	const [typed, setTyped] = useState("");
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);
	const typedFieldId = useId();

	const locked = confirmDisabled || (requireTypedText !== undefined && typed !== requireTypedText);

	async function confirm() {
		setBusy(true);
		setFailure(null);
		try {
			await onConfirm();
			onOpenChange(false);
			setTyped("");
		} catch (error) {
			// Stays open, and says why inside the dialog (ADJ-M03-1). It used to
			// leave the reason to the caller, and fifteen callers never gave one:
			// Confirm spun, stopped, and nothing happened.
			setFailure(errorMessage(error, t, errorKeyPrefix));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!busy) {
					setFailure(null);
					onOpenChange(next);
				}
			}}
			title={title}
			description={description}
			size="sm"
			closeLabel={closeLabel ?? t("ui.actions.close")}
			footer={
				<>
					<Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
						{cancelLabel}
					</Button>
					<Button
						variant={tone === "danger" ? "danger" : "primary"}
						onClick={() => void confirm()}
						loading={busy}
						disabled={locked}
					>
						{confirmLabel}
					</Button>
				</>
			}
		>
			{children}
			{requireTypedText !== undefined && (
				<div className="flex flex-col gap-1.5">
					<Label htmlFor={typedFieldId}>{typedTextLabel}</Label>
					<Input
						id={typedFieldId}
						value={typed}
						onChange={(event) => setTyped(event.target.value)}
						// Every one of these is off deliberately. A browser offering to
						// autocomplete the name of the thing somebody is about to
						// destroy, or silently capitalising it so it no longer matches,
						// defeats the entire point of asking them to type it.
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="off"
						spellCheck={false}
						className="font-mono"
					/>
				</div>
			)}
			{failure !== null && (
				<p
					role="alert"
					className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-[12.5px] text-danger-fg"
				>
					{failure}
				</p>
			)}
		</Dialog>
	);
}
